package org.teddapp.project.ui.backup

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddFilterRow
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.teddClickable

/**
 * Briques de l'écran Sauvegarde en mise en page étendue : bandeau d'écart, colonne des
 * onglets, état de la connexion et de la synchronisation.
 */

@Composable
internal fun PulseDot(color: Color, size: androidx.compose.ui.unit.Dp = 6.dp, animate: Boolean = true) {
    if (!animate) {
        Box(modifier = Modifier.size(size).background(color, CircleShape))
        return
    }
    val transition = rememberInfiniteTransition(label = "pulse")
    val alpha by transition.animateFloat(
        initialValue = 1f,
        targetValue = 0.35f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 1600, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse,
        ),
        label = "pulse-alpha",
    )
    Box(modifier = Modifier.size(size).alpha(alpha).background(color, CircleShape))
}

// ── Bandeau d'écart ────────────────────────────────────────────────────

/** Dit qu'une synchronisation tarde ou a échoué. Le signal porte toujours une icône et un mot. */
@Composable
internal fun GapBanner(
    gap: BackupGap,
    onDismiss: () -> Unit,
    onRunNow: () -> Unit,
) {
    val (color, soft, icon) = when (gap.level) {
        BackupGapLevel.Danger -> Triple(TeddTokens.Danger, TeddTokens.DangerSoft, "alert")
        BackupGapLevel.Warning -> Triple(TeddTokens.Warning, TeddTokens.WarningSoft, "alert")
        BackupGapLevel.Info -> Triple(TeddTokens.InkMuted, TeddTokens.SurfaceSunken, "clock")
        BackupGapLevel.Ok -> Triple(TeddTokens.Success, TeddTokens.SuccessSoft, "check")
    }
    val label = when (gap.level) {
        BackupGapLevel.Danger -> "Alerte"
        BackupGapLevel.Warning -> "Avertissement"
        BackupGapLevel.Info -> "Information"
        BackupGapLevel.Ok -> "À jour"
    }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(soft, RoundedCornerShape(TeddTokens.RMd))
            .padding(start = TeddSpace.S4, end = TeddSpace.S2, top = TeddSpace.S2, bottom = TeddSpace.S2),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
    ) {
        TeddIcon(name = icon, size = 18.dp, color = color)
        Column(modifier = Modifier.weight(1f)) {
            Text(label, color = color, style = TeddType.Label)
            Text(gap.message, color = TeddTokens.Ink, style = TeddType.Caption)
        }
        TeddButton(text = "Synchroniser", onClick = onRunNow, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm, icon = "repeat")
        TeddIconButton(onClick = onDismiss) {
            TeddIcon(name = "x", size = 16.dp, color = TeddTokens.InkMuted)
        }
    }
}

// ── Colonne des onglets ────────────────────────────────────────────────

@Composable
internal fun BackupSidebar(
    tab: BackupTab,
    onTabChange: (BackupTab) -> Unit,
    historyCount: Int,
    cloudConnected: Boolean,
    projectUrl: String?,
    syncEnabled: Boolean,
    onToggleSync: (Boolean) -> Unit,
    syncAvailable: Boolean,
    pendingChanges: Int,
    syncing: Boolean,
) {
    // Le filet du bord est la poignée : on y règle la largeur de la colonne.
    org.teddapp.project.ui.TeddSidePane(
        spec = BACKUP_PANE,
        collapsedContent = { expand ->
            org.teddapp.project.ui.TeddPaneRail(
                expand = expand,
                title = "les onglets",
                items = BackupTab.entries.map { t -> org.teddapp.project.ui.PaneRailItem(t.name, t.label, t.icon, tab == t) { onTabChange(t) } },
            )
        },
    ) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(2.dp),
    ) {
        Text(
            text = "SAUVEGARDE",
            style = TeddType.Overline,
            color = TeddTokens.InkMuted,
            modifier = Modifier.padding(start = 10.dp, bottom = 6.dp),
        )
        BackupTab.entries.forEach { t ->
            SidebarTabRow(
                tab = t,
                isActive = tab == t,
                badge = if (t == BackupTab.History) historyCount else null,
                onClick = { onTabChange(t) },
            )
        }
        Spacer(modifier = Modifier.height(TeddSpace.S4))
        SupabaseCard(connected = cloudConnected, projectUrl = projectUrl)
        Spacer(modifier = Modifier.height(TeddSpace.S2))
        SyncLiveCard(
            available = syncAvailable,
            enabled = syncEnabled,
            pendingChanges = pendingChanges,
            syncing = syncing,
            onToggle = onToggleSync,
        )
    }
    }
}

/** Les onglets de la sauvegarde : 248 dp par défaut, de 200 à 400. */
private val BACKUP_PANE = org.teddapp.project.ui.PaneSpec("backup.sidebar", default = 248.dp, min = 200.dp, max = 400.dp, collapseBelow = 150.dp)

@Composable
internal fun SidebarTabRow(
    tab: BackupTab,
    isActive: Boolean,
    badge: Int?,
    onClick: () -> Unit,
) {
    TeddFilterRow(
        label = tab.label,
        selected = isActive,
        onClick = onClick,
        count = badge,
        leading = { TeddIcon(name = tab.icon, size = 16.dp, color = if (isActive) TeddTokens.Ink else TeddTokens.InkMuted) },
    )
}

/** Petite carte d'état : une icône qui porte le signal, un titre, une précision. */
@Composable
private fun StatusCard(
    icon: String,
    iconColor: Color,
    title: String,
    detail: String,
    note: String? = null,
    trailing: @Composable () -> Unit = {},
) {
    TeddCard(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(TeddTokens.RMd),
        padding = PaddingValues(TeddSpace.S3),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
            TeddIcon(name = icon, size = 18.dp, color = iconColor)
            Column(modifier = Modifier.weight(1f)) {
                Text(title, color = TeddTokens.Ink, style = TeddType.Label, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(detail, color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp), maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
            trailing()
        }
        if (note != null) {
            Text(note, color = TeddTokens.InkMuted, style = TeddType.Caption, modifier = Modifier.padding(top = TeddSpace.S2))
        }
    }
}

@Composable
internal fun SupabaseCard(connected: Boolean, projectUrl: String?) {
    StatusCard(
        icon = if (connected) "check" else "globe",
        iconColor = if (connected) TeddTokens.Success else TeddTokens.InkMuted,
        title = if (connected) "Serveur relié" else "Aucun serveur",
        detail = projectUrl ?: "non configuré",
        note = when {
            connected -> "Synchronisation active, à la demande ou selon la fréquence choisie."
            projectUrl != null -> "Serveur enregistré, synchronisation coupée. Active-la ci-dessous."
            else -> "Tout fonctionne en local. Connecte-toi au serveur teddApp dans les Réglages pour synchroniser."
        },
    )
}

// ── Interrupteur ───────────────────────────────────────────────────────

/** Allumé : piste citron vert, bouton d'encre. */
@Composable
internal fun ToggleSwitch(on: Boolean, onChange: () -> Unit, small: Boolean = false) {
    val w = if (small) 32.dp else 36.dp
    val h = if (small) 18.dp else 20.dp
    val shape = RoundedCornerShape(TeddTokens.RFull)
    Box(
        modifier = Modifier
            .size(w, h)
            .teddClickable(
                onClick = onChange,
                shape = shape,
                fill = if (on) TeddTokens.Lime else TeddTokens.LineStrong,
                hover = if (on) TeddTokens.Lime else TeddTokens.LineStrong,
                role = Role.Switch,
            )
            .padding(2.dp),
    ) {
        Box(
            modifier = Modifier
                .size(h - 4.dp)
                .align(if (on) Alignment.CenterEnd else Alignment.CenterStart)
                .background(if (on) TeddTokens.OnLime else TeddTokens.Surface, CircleShape),
        )
    }
}

// ── État de la synchronisation ─────────────────────────────────────────

@Composable
internal fun SyncLiveCard(
    available: Boolean,
    enabled: Boolean,
    pendingChanges: Int,
    syncing: Boolean,
    onToggle: (Boolean) -> Unit,
) {
    StatusCard(
        icon = when {
            !available -> "lock"
            syncing -> "repeat"
            enabled -> "check"
            else -> "pause"
        },
        iconColor = if (available && enabled && !syncing) TeddTokens.Success else TeddTokens.InkMuted,
        title = when {
            !available -> "Synchro indisponible"
            syncing -> "Synchro en cours…"
            enabled -> "Synchro active"
            else -> "Synchro coupée"
        },
        detail = when {
            !available -> "connecte-toi d'abord au serveur"
            pendingChanges == 0 -> "rien en attente"
            else -> "$pendingChanges changement${if (pendingChanges > 1) "s" else ""} en attente"
        },
        trailing = {
            if (available) ToggleSwitch(on = enabled, onChange = { onToggle(!enabled) }, small = true)
        },
    )
}
